import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/clinic/Navbar";
import Footer from "@/components/clinic/Footer";
import { useToast } from "@/hooks/use-toast";
import { useLiveClinic } from "@/lib/clinicLive";
import { Copy, ExternalLink, Eye, Smartphone, Tablet, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";

type Device = "mobile" | "ipad" | "desktop";

interface DeviceConfig {
  id: Device;
  label: string;
  /** Width hint shown to the user — the iframe itself is fluid (width: 100%). */
  widthLabel: string;
  /** Initial iframe height before the auto-resize listener kicks in. */
  initialHeight: number;
  Icon: typeof Smartphone;
}

const DEVICES: DeviceConfig[] = [
  { id: "mobile", label: "Mobile", widthLabel: "≤ 480 px", initialHeight: 5200, Icon: Smartphone },
  { id: "ipad", label: "iPad / Tablet", widthLabel: "481 – 1024 px", initialHeight: 4400, Icon: Tablet },
  { id: "desktop", label: "Desktop", widthLabel: "≥ 1025 px", initialHeight: 3800, Icon: Monitor },
];

/**
 * Build the HTML snippet the user pastes into a Wix "Embed HTML" element.
 *
 * The snippet:
 *   - Renders an <iframe> pointed at /embed on the published site.
 *   - Listens for postMessage events from the embed and resizes the iframe
 *     so visitors never see an inner scrollbar.
 *   - Falls back to a sensible initial height per device profile so there's
 *     no layout shift while the first message arrives.
 */
const buildSnippet = (origin: string, device: DeviceConfig): string => {
  const src = `${origin.replace(/\/$/, "")}/embed`;
  const id = `dr-vahe-embed-${device.id}`;
  return `<!-- Dr Vahe Clinic — ${device.label} embed (${device.widthLabel}) -->
<div style="width:100%;max-width:100%;margin:0 auto;">
  <iframe
    id="${id}"
    src="${src}"
    title="Dr Vahe Clinic"
    loading="lazy"
    referrerpolicy="no-referrer-when-downgrade"
    allow="clipboard-write; fullscreen; payment"
    style="width:100%;border:0;display:block;min-height:${device.initialHeight}px;background:#fff;"
  ></iframe>
</div>
<script>
  (function () {
    var frame = document.getElementById(${JSON.stringify(id)});
    if (!frame) return;
    window.addEventListener('message', function (e) {
      var d = e && e.data;
      if (!d || d.source !== 'dr-vahe' || d.type !== 'lovable-embed:height') return;
      var h = Math.max(320, parseInt(d.height, 10) || 0);
      if (h && Math.abs(h - frame.clientHeight) > 4) frame.style.height = h + 'px';
    }, false);
  })();
</script>`;
};

const EmbedSnippets = () => {
  const { toast } = useToast();
  const live = useLiveClinic();
  const [device, setDevice] = useState<Device>("mobile");

  // The published Lovable URL is the origin Wix iframes will point at.
  const origin = "https://www.blepharoplasty.drvaheclinic.com";
  const previewUrl = `${origin}/embed?embed=1`;

  const config = DEVICES.find((d) => d.id === device)!;
  const snippet = useMemo(() => buildSnippet(origin, config), [origin, config]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast({
        title: `${config.label} snippet copied`,
        description: "Paste it into a Wix → Embed → HTML iframe element.",
      });
    } catch {
      toast({
        title: "Copy failed",
        description: "Select the text manually and copy with ⌘/Ctrl + C.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <article className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Link
            to="/admin/clinic-settings"
            className="text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70 transition-colors"
          >
            ← Back to clinic settings
          </Link>
          <header className="mt-8 mb-10">
            <span className="text-[10px] tracking-[0.35em] uppercase text-accent font-medium">
              Admin · Wix embed
            </span>
            <h1 className="font-display text-4xl md:text-5xl mt-4 text-foreground">
              Embed in <span className="italic">Wix</span>
            </h1>
            <p className="mt-4 text-muted-foreground font-light max-w-2xl leading-relaxed">
              Paste the snippet below into a Wix <strong>Embed&nbsp;Code → HTML iframe</strong>
              {" "}element. The iframe automatically resizes to fit {live.name}'s content
              on mobile, iPad and desktop — no scrollbars, no fixed height to maintain.
            </p>
          </header>

          {/* Device picker */}
          <div className="grid sm:grid-cols-3 gap-3 mb-8">
            {DEVICES.map((d) => {
              const active = d.id === device;
              const Icon = d.Icon;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setDevice(d.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                    active
                      ? "border-accent bg-accent/10 text-foreground"
                      : "border-border bg-background text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  }`}
                  aria-pressed={active}
                >
                  <Icon className={`h-4 w-4 ${active ? "text-accent" : ""}`} />
                  <div className="min-w-0">
                    <p className="text-[11px] tracking-[0.25em] uppercase font-medium">
                      {d.label}
                    </p>
                    <p className="text-[10px] opacity-70">{d.widthLabel}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Snippet */}
          <section className="bg-background border border-border rounded-2xl overflow-hidden mb-8">
            <header className="px-5 py-3 border-b border-border bg-linen-deep flex items-center justify-between gap-3">
              <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
                {config.label} snippet
              </p>
              <div className="flex items-center gap-3">
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70"
                >
                  <Eye className="h-3 w-3" />
                  Preview /embed
                </a>
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70"
                >
                  <Copy className="h-3 w-3" />
                  Copy
                </button>
              </div>
            </header>
            <pre className="px-5 py-4 text-[11px] leading-relaxed text-foreground/85 font-mono max-h-[360px] overflow-auto whitespace-pre-wrap break-words">
              {snippet}
            </pre>
          </section>

          {/* Live preview */}
          <section className="bg-background border border-border rounded-2xl overflow-hidden mb-12">
            <header className="px-5 py-3 border-b border-border bg-linen-deep">
              <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
                Live preview · {config.label}
              </p>
            </header>
            <div className="bg-muted/30 p-4 md:p-6 flex justify-center">
              <div
                className="bg-background border border-border rounded-xl overflow-hidden shadow-sm"
                style={{
                  width:
                    device === "mobile"
                      ? "min(100%, 390px)"
                      : device === "ipad"
                      ? "min(100%, 820px)"
                      : "100%",
                }}
              >
                <iframe
                  key={device}
                  src={previewUrl}
                  title={`${config.label} preview`}
                  loading="lazy"
                  className="block w-full"
                  style={{ height: `${Math.min(900, config.initialHeight)}px`, border: 0 }}
                />
              </div>
            </div>
          </section>

          {/* How-to */}
          <section className="space-y-4 text-sm text-muted-foreground font-light leading-relaxed">
            <h2 className="font-display text-xl text-foreground">How to add this to Wix</h2>
            <ol className="list-decimal pl-5 space-y-2">
              <li>
                In your Wix editor, click <strong>Add (+) → Embed Code → Embed HTML</strong>
                {" "}(or use a "Custom Element" / "HTML iframe" widget).
              </li>
              <li>
                Choose <strong>Code</strong> and paste the snippet above. Click <strong>Update</strong>.
              </li>
              <li>
                Stretch the Wix element to <strong>full width</strong> and set its height
                to roughly {config.initialHeight}px — the snippet will then auto-grow to
                match the real content height on every device.
              </li>
              <li>
                Repeat for the other two device profiles if you want different starting
                heights per breakpoint, or use the mobile snippet for all three (it works
                everywhere — only the initial height differs).
              </li>
              <li>
                Publish your Wix site and reload to confirm there is no inner scrollbar.
              </li>
            </ol>

            <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 mt-6">
              <p className="text-[10px] tracking-[0.3em] uppercase text-amber-700 dark:text-amber-400 mb-2">
                If Wix shows a blank box
              </p>
              <p>
                Lovable's free <code className="font-mono text-foreground">.lovable.app</code> URL
                may be served with frame-blocking headers. If the iframe stays blank,
                connect a custom domain (e.g. <code className="font-mono text-foreground">embed.doctorvahe.com</code>)
                in <strong>Project&nbsp;→&nbsp;Settings&nbsp;→&nbsp;Domains</strong>, then change
                the <code className="font-mono text-foreground">src</code> in the snippet to that
                domain. A custom domain has no frame restrictions and will load reliably
                inside Wix.
              </p>
            </div>

            <div className="pt-4">
              <Button asChild variant="outline" size="sm">
                <a
                  href="https://support.wix.com/en/article/wix-editor-adding-html-code-to-your-site"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Wix help: Adding HTML code <ExternalLink className="h-3.5 w-3.5 ml-1" />
                </a>
              </Button>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default EmbedSnippets;
